import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { hubSectionElementId } from '@/components/Hub/hubSectionTypes';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

const LANDING_GAP_PX = 24;

// Where scrollend is missing, a jump counts as over once scrolling has gone quiet this long.
const SCROLL_IDLE_MS = 200;

/**
 * `navRef` must be a direct child of the sheet's scroll area. Scrolling that element rather than
 * calling `scrollIntoView` keeps the page under the sheet from scrolling too.
 */
export const useHubActiveSection = (ids: readonly string[], navRef: RefObject<HTMLElement | null>) => {
    const [activeId, setActiveId] = useState<string | undefined>(ids[0]);
    const prefersReducedMotion = usePrefersReducedMotion();

    // Holds a tapped section active while the scroll passes the ones between.
    const jumpTargetRef = useRef<string | null>(null);
    const jumpFallbackRef = useRef<number | undefined>(undefined);

    const releaseJumpWhenIdle = useCallback(() => {
        window.clearTimeout(jumpFallbackRef.current);
        jumpFallbackRef.current = window.setTimeout(() => {
            jumpTargetRef.current = null;
        }, SCROLL_IDLE_MS);
    }, []);

    const idsKey = ids.join(' ');

    useEffect(() => {
        const nav = navRef.current;
        const scroller = nav?.parentElement;
        if (!nav || !scroller || ids.length === 0) {
            return;
        }

        const update = () => {
            if (jumpTargetRef.current) {
                return;
            }
            // Low enough that one wheel step can't carry a section's top past it and under the nav.
            const navBottom = nav.getBoundingClientRect().bottom;
            const readingLine = navBottom + (scroller.getBoundingClientRect().bottom - navBottom) / 3;
            let next = ids[0];
            for (const id of ids) {
                const element = document.getElementById(hubSectionElementId(id));
                if (element && element.getBoundingClientRect().top <= readingLine) {
                    next = id;
                }
            }
            // A short last section can never reach the reading line.
            const isAtBottom = scroller.scrollTop > 0
                && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
            setActiveId(isAtBottom ? ids[ids.length - 1] : next);
        };

        let frame = 0;
        const scheduleUpdate = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        };
        const onScroll = () => {
            if (jumpTargetRef.current) {
                releaseJumpWhenIdle();
            }
            scheduleUpdate();
        };
        // No update here. A jump near the bottom stops short of the reading line and would lose its target.
        const onScrollEnd = () => {
            jumpTargetRef.current = null;
        };

        // A section that finishes loading moves the ones below it without a scroll.
        const resizeObserver = new ResizeObserver(scheduleUpdate);
        for (const id of ids) {
            const element = document.getElementById(hubSectionElementId(id));
            if (element) {
                resizeObserver.observe(element);
            }
        }

        update();
        scroller.addEventListener('scroll', onScroll, { passive: true });
        scroller.addEventListener('scrollend', onScrollEnd);
        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            scroller.removeEventListener('scroll', onScroll);
            scroller.removeEventListener('scrollend', onScrollEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey, navRef, releaseJumpWhenIdle]);

    useEffect(() => () => window.clearTimeout(jumpFallbackRef.current), []);

    const scrollToSection = useCallback((id: string) => {
        const nav = navRef.current;
        const scroller = nav?.parentElement;
        const element = document.getElementById(hubSectionElementId(id));
        if (!nav || !scroller || !element) {
            return;
        }

        setActiveId(id);
        const offset = element.getBoundingClientRect().top - nav.getBoundingClientRect().bottom - LANDING_GAP_PX;
        const top = Math.min(Math.max(scroller.scrollTop + offset, 0), scroller.scrollHeight - scroller.clientHeight);

        // Already in place, so no scroll or scrollend would come to end a hold.
        if (Math.abs(top - scroller.scrollTop) < 1) {
            window.clearTimeout(jumpFallbackRef.current);
            jumpTargetRef.current = null;
        }
        else {
            jumpTargetRef.current = id;
            releaseJumpWhenIdle();
            scroller.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
        }
        // `preventScroll` so the browser's own focus scroll doesn't cut the smooth scroll short.
        element.focus({ preventScroll: true });
    }, [navRef, prefersReducedMotion, releaseJumpWhenIdle]);

    return { activeId, scrollToSection };
};
